/**
 * Task #262 — Reusable monthly calendar primitive.
 *
 * USAGE NOTES (READ BEFORE EMBEDDING):
 * - This component renders a Mon-first month grid and exposes a fully
 *   generic `renderCell` render-prop. It knows NOTHING about Time Off.
 * - The internal cell wrapper is `React.memo`-ised with a stable
 *   comparator (date + selection state + same renderCell identity). For
 *   the memo to actually skip re-renders, ALWAYS pass `renderCell`,
 *   `topRightSlot`/`bottomRightSlot` (`cellSlot` aliases) wrapped in
 *   `useCallback` from the consumer. Inline arrow functions defeat
 *   memoisation.
 * - Events are pre-indexed by date and the same array reference is
 *   handed to a cell on every render as long as the events for that day
 *   haven't changed — including a stable shared `EMPTY` array for days
 *   with no events — so the memo comparator's `a.eventsOnDay === b.eventsOnDay`
 *   check actually short-circuits.
 * - Selection is range-based (click to start, click to end, drag also
 *   works). Pass `onRangeChange` to receive the inclusive [startYmd,
 *   endYmd] pair when selection settles.
 * - The renderCell receives a `holiday` field (the first event whose
 *   `kind === 'holiday'`, if any) plus a `selectionState` enum so
 *   consumers can paint range start/middle/end differently in one read.
 * - Holidays / events are opaque to the calendar; the default renderer
 *   draws the day number plus up to 3 coloured dots (one per event,
 *   colour resolved via the event's optional `color` field) so consumers
 *   get a reasonable look out of the box without writing a renderCell.
 *
 * INLINE USAGE EXAMPLE — copy/paste into a consumer:
 *
 *   // EXAMPLE: a project-deadline calendar with custom badges. Both the
 *   // event list and the cell render-prop are memoised so all 42 cells
 *   // skip re-rendering when the surrounding component re-renders.
 *   const events = useMemo<MonthCalendarEvent[]>(() => projects.map(p => ({
 *     date: p.due, kind: 'deadline', color: p.color, label: p.name,
 *   })), [projects]);
 *
 *   const renderCell = useCallback((args: RenderCellArgs) => (
 *     <div className="flex flex-col h-full">
 *       <span className="text-[10px]">{Number(args.date.slice(8,10))}</span>
 *       {args.eventsOnDay.map((e,i) => (
 *         <span key={i} className="text-[8px] rounded px-1" style={{ background: e.color }}>
 *           {e.label}
 *         </span>
 *       ))}
 *     </div>
 *   ), []);
 *
 *   const topRightSlot = useCallback((args: RenderCellArgs) => (
 *     args.eventsOnDay.length > 0
 *       ? <span className="text-[9px]">{args.eventsOnDay.length}</span>
 *       : null
 *   ), []);
 *
 *   return (
 *     <MonthCalendar
 *       year={year} month={month}
 *       events={events}
 *       renderCell={renderCell}
 *       topRightSlot={topRightSlot}
 *       onRangeChange={setRange}
 *     />
 *   );
 */
import { memo, useCallback, useMemo, useState, type ReactNode, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatInTimeZone } from "date-fns-tz";

// Project-wide rule: all date math in user-facing calendars runs in
// Europe/Berlin (Hamburg). Without this, "today" and the month grid
// would drift for users whose browser locale is set to a different
// timezone, which would in turn flip holiday/business-day calculations
// off-by-one near midnight.
const BERLIN_TZ = "Europe/Berlin";

export interface MonthCalendarEvent {
  date: string; // YYYY-MM-DD
  /** Optional CSS color for the default dot renderer (e.g. "#ef4444"). */
  color?: string;
  /** Optional tooltip text used by the default dot renderer. */
  label?: string;
  [k: string]: any;
}

export interface MonthCalendarRange {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD inclusive
}

export type CellSelectionState = "none" | "start" | "middle" | "end" | "single";

export interface RenderCellArgs<E extends MonthCalendarEvent = MonthCalendarEvent> {
  date: string; // YYYY-MM-DD
  isToday: boolean;
  isWeekend: boolean;
  isInCurrentMonth: boolean;
  /** First event on this day whose `kind === 'holiday'` (if any). */
  holiday: E | null;
  /** All events the consumer pushed onto this day, in input order. */
  eventsOnDay: E[];
  /** Convenience flag — equivalent to `selectionState !== 'none'`. */
  inSelection: boolean;
  isSelectionStart: boolean;
  isSelectionEnd: boolean;
  selectionState: CellSelectionState;
  defaultContent: ReactNode;
  /** Back-compat alias for `eventsOnDay` (kept so legacy renderers keep working). */
  events: E[];
}

export interface MonthCalendarProps<E extends MonthCalendarEvent = MonthCalendarEvent> {
  year: number;
  month: number; // 1..12
  events?: E[];
  selectedRange?: MonthCalendarRange | null;
  onRangeChange?: (range: MonthCalendarRange | null) => void;
  onMonthChange?: (year: number, month: number) => void;
  /** Render a cell. MUST be wrapped in useCallback by the consumer. */
  renderCell?: (args: RenderCellArgs<E>) => ReactNode;
  /**
   * Top-right corner hook per cell — useful for badge counts or icons
   * that should overlay every day. MUST be wrapped in useCallback.
   * (`cornerSlot` is kept as an alias for backwards compatibility.)
   */
  topRightSlot?: (args: RenderCellArgs<E>) => ReactNode;
  /** Alias for `topRightSlot`. */
  cornerSlot?: (args: RenderCellArgs<E>) => ReactNode;
  /** Bottom-right corner hook per cell. MUST be wrapped in useCallback. */
  bottomRightSlot?: (args: RenderCellArgs<E>) => ReactNode;
  /** Optional content rendered in the top-right of the calendar header. */
  headerSlot?: ReactNode;
  /** Disable user interaction (read-only display). */
  readOnly?: boolean;
  className?: string;
}

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Shared empty-events reference so the memo comparator on every empty
// cell stays `===` across renders. Without this, every parent state
// change re-renders all 42 cells.
const EMPTY_EVENTS: readonly MonthCalendarEvent[] = Object.freeze([]);

/** Berlin-local "today" as YYYY-MM-DD. */
function todayInBerlin(): string {
  return formatInTimeZone(new Date(), BERLIN_TZ, "yyyy-MM-dd");
}

/** Berlin-local DOW (0=Sun..6=Sat) for a YYYY-MM-DD string. */
function berlinDow(ymdStr: string): number {
  return Number(formatInTimeZone(new Date(`${ymdStr}T12:00:00Z`), BERLIN_TZ, "i")) % 7;
}

/** Add N days to YYYY-MM-DD, anchoring at Berlin noon to avoid DST jumps. */
function addDaysYmd(ymdStr: string, n: number): string {
  // Anchor at 12:00 UTC and add days — DST shifts are <12h so the
  // Berlin-local calendar date is preserved across the addition.
  const d = new Date(`${ymdStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return formatInTimeZone(d, BERLIN_TZ, "yyyy-MM-dd");
}

function buildGrid(year: number, month: number): string[] {
  // First of month in Berlin time.
  const firstYmd = `${year}-${String(month).padStart(2, "0")}-01`;
  // Convert Berlin DOW to Mon-first offset.
  const berlinJsDow = berlinDow(firstYmd); // 0..6 with Sun=0
  const offset = (berlinJsDow + 6) % 7;    // Mon=0..Sun=6
  const startYmd = addDaysYmd(firstYmd, -offset);
  const out: string[] = [];
  for (let i = 0; i < 42; i++) out.push(addDaysYmd(startYmd, i));
  return out;
}

interface CellWrapperProps {
  date: string;
  isToday: boolean;
  isWeekend: boolean;
  isInCurrentMonth: boolean;
  selectionState: CellSelectionState;
  events: MonthCalendarEvent[] | readonly MonthCalendarEvent[];
  renderCell?: MonthCalendarProps["renderCell"];
  topRightSlot?: MonthCalendarProps["topRightSlot"];
  bottomRightSlot?: MonthCalendarProps["bottomRightSlot"];
  readOnly?: boolean;
  onMouseDown: (date: string) => void;
  onMouseEnter: (date: string) => void;
  onMouseUp: (date: string) => void;
}

function defaultRenderer(args: RenderCellArgs): ReactNode {
  const events = args.eventsOnDay;
  return (
    <div className="flex flex-col h-full">
      <span className={cn("text-[10px] leading-none", args.isInCurrentMonth ? "text-gray-200" : "text-gray-500", args.holiday && "text-rose-300 font-medium")}>
        {Number(args.date.slice(8, 10))}
      </span>
      {events.length > 0 && (
        <div className="flex gap-0.5 mt-0.5 flex-wrap">
          {events.slice(0, 3).map((e, i) => (
            <span
              key={i}
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: e.color ?? "#60a5fa" }}
              title={e.label}
            />
          ))}
          {events.length > 3 && <span className="text-[8px] text-gray-400">+{events.length - 3}</span>}
        </div>
      )}
    </div>
  );
}

const CellWrapper = memo(function CellWrapper(props: CellWrapperProps) {
  const {
    date, isToday, isWeekend, isInCurrentMonth, selectionState,
    events, renderCell, topRightSlot, bottomRightSlot, readOnly,
    onMouseDown, onMouseEnter, onMouseUp,
  } = props;
  const eventsArr = events as MonthCalendarEvent[];
  const holiday = eventsArr.find((e) => (e as any).kind === "holiday") ?? null;
  const inSelection = selectionState !== "none";
  const isSelectionStart = selectionState === "start" || selectionState === "single";
  const isSelectionEnd = selectionState === "end" || selectionState === "single";
  const args: RenderCellArgs = {
    date, isToday, isWeekend, isInCurrentMonth,
    holiday: holiday as any,
    eventsOnDay: eventsArr,
    events: eventsArr,
    inSelection, isSelectionStart, isSelectionEnd, selectionState,
    defaultContent: null,
  };
  args.defaultContent = defaultRenderer(args);
  const inner = renderCell ? renderCell(args) : args.defaultContent;
  const tr = topRightSlot ? topRightSlot(args) : null;
  const br = bottomRightSlot ? bottomRightSlot(args) : null;
  return (
    <div
      data-date={date}
      onMouseDown={readOnly ? undefined : () => onMouseDown(date)}
      onMouseEnter={readOnly ? undefined : () => onMouseEnter(date)}
      onMouseUp={readOnly ? undefined : () => onMouseUp(date)}
      className={cn(
        "relative min-h-[44px] border border-gray-700 p-1 select-none transition-colors",
        isInCurrentMonth ? "bg-gray-900" : "bg-gray-900/40",
        isWeekend && isInCurrentMonth && "bg-gray-800/70",
        isToday && "ring-1 ring-blue-500/60",
        inSelection && "bg-blue-900/40 border-blue-600/60",
        (isSelectionStart || isSelectionEnd) && "bg-blue-700/60 border-blue-400",
        !readOnly && "cursor-pointer hover:bg-blue-900/30",
      )}
    >
      {inner}
      {tr != null && (
        <div className="absolute top-0.5 right-0.5 pointer-events-none">{tr}</div>
      )}
      {br != null && (
        <div className="absolute bottom-0.5 right-0.5 pointer-events-none">{br}</div>
      )}
    </div>
  );
}, (a, b) => (
  a.date === b.date &&
  a.isToday === b.isToday &&
  a.isWeekend === b.isWeekend &&
  a.isInCurrentMonth === b.isInCurrentMonth &&
  a.selectionState === b.selectionState &&
  a.events === b.events &&
  a.renderCell === b.renderCell &&
  a.topRightSlot === b.topRightSlot &&
  a.bottomRightSlot === b.bottomRightSlot &&
  a.readOnly === b.readOnly &&
  a.onMouseDown === b.onMouseDown &&
  a.onMouseEnter === b.onMouseEnter &&
  a.onMouseUp === b.onMouseUp
));

export function MonthCalendar(props: MonthCalendarProps) {
  const {
    year, month, events, selectedRange, onRangeChange,
    onMonthChange, renderCell, cornerSlot, topRightSlot, bottomRightSlot,
    headerSlot, readOnly, className,
  } = props;
  const grid = useMemo(() => buildGrid(year, month), [year, month]);
  const today = useMemo(() => todayInBerlin(), []);

  // Index events by date with stable references.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, MonthCalendarEvent[]>();
    for (const e of events ?? []) {
      const arr = map.get(e.date);
      if (arr) arr.push(e); else map.set(e.date, [e]);
    }
    return map;
  }, [events]);

  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);

  useEffect(() => {
    if (!dragStart) return;
    const onUp = () => {
      if (dragStart && dragEnd && onRangeChange) {
        const lo = dragStart < dragEnd ? dragStart : dragEnd;
        const hi = dragStart < dragEnd ? dragEnd : dragStart;
        onRangeChange({ start: lo, end: hi });
      }
      setDragStart(null);
      setDragEnd(null);
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [dragStart, dragEnd, onRangeChange]);

  const handleMouseDown = useCallback((date: string) => {
    setDragStart(date);
    setDragEnd(date);
  }, []);
  const handleMouseEnter = useCallback((date: string) => {
    setDragStart((s) => { if (s) setDragEnd(date); return s; });
  }, []);
  const handleMouseUp = useCallback((date: string) => {
    setDragStart((s) => {
      if (s && onRangeChange) {
        if (s === date) {
          // No drag — pure click. Two-click range selection: if a
          // single-day range is already selected and the new click is
          // on a LATER date, extend it. Same date toggles to a
          // single-day pick; earlier date restarts at the new pick.
          if (
            selectedRange &&
            selectedRange.start === selectedRange.end &&
            date > selectedRange.start
          ) {
            onRangeChange({ start: selectedRange.start, end: date });
          } else {
            onRangeChange({ start: date, end: date });
          }
        } else {
          const lo = s < date ? s : date;
          const hi = s < date ? date : s;
          onRangeChange({ start: lo, end: hi });
        }
      }
      setDragEnd(null);
      return null;
    });
  }, [onRangeChange, selectedRange]);

  const liveRange: MonthCalendarRange | null = dragStart && dragEnd
    ? { start: dragStart < dragEnd ? dragStart : dragEnd, end: dragStart < dragEnd ? dragEnd : dragStart }
    : (selectedRange ?? null);

  const prev = useCallback(() => {
    if (!onMonthChange) return;
    if (month === 1) onMonthChange(year - 1, 12);
    else onMonthChange(year, month - 1);
  }, [year, month, onMonthChange]);
  const next = useCallback(() => {
    if (!onMonthChange) return;
    if (month === 12) onMonthChange(year + 1, 1);
    else onMonthChange(year, month + 1);
  }, [year, month, onMonthChange]);
  const jumpToToday = useCallback(() => {
    if (!onMonthChange) return;
    const y = Number(today.slice(0, 4));
    const m = Number(today.slice(5, 7));
    if (y !== year || m !== month) onMonthChange(y, m);
  }, [today, year, month, onMonthChange]);
  const isOnTodayMonth =
    year === Number(today.slice(0, 4)) && month === Number(today.slice(5, 7));

  return (
    <div className={cn("rounded-md border border-gray-700 bg-gray-900/30 p-2", className)}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={prev} disabled={!onMonthChange}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium text-gray-100 px-1">
            {MONTH_NAMES[month - 1]} {year}
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={next} disabled={!onMonthChange}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs ml-1"
            onClick={jumpToToday}
            disabled={!onMonthChange || isOnTodayMonth}
            title="Jump to today's month"
          >
            Today
          </Button>
        </div>
        {headerSlot}
      </div>
      <div className="grid grid-cols-7 gap-px text-[10px] text-gray-400 mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-1 py-0.5 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {grid.map((date) => {
          const dow = berlinDow(date);
          const isInCurrentMonth = Number(date.slice(5, 7)) === month;
          let selectionState: CellSelectionState = "none";
          if (liveRange) {
            const isStart = date === liveRange.start;
            const isEnd = date === liveRange.end;
            if (isStart && isEnd) selectionState = "single";
            else if (isStart) selectionState = "start";
            else if (isEnd) selectionState = "end";
            else if (date > liveRange.start && date < liveRange.end) selectionState = "middle";
          }
          const eventsForDay = eventsByDate.get(date) ?? EMPTY_EVENTS;
          return (
            <CellWrapper
              key={date}
              date={date}
              isToday={date === today}
              isWeekend={dow === 0 || dow === 6}
              isInCurrentMonth={isInCurrentMonth}
              selectionState={selectionState}
              events={eventsForDay}
              renderCell={renderCell}
              topRightSlot={topRightSlot ?? cornerSlot}
              bottomRightSlot={bottomRightSlot}
              readOnly={readOnly}
              onMouseDown={handleMouseDown}
              onMouseEnter={handleMouseEnter}
              onMouseUp={handleMouseUp}
            />
          );
        })}
      </div>
    </div>
  );
}

export default MonthCalendar;
